import React, { useContext, useEffect, useMemo, useState } from "react";
import { Classifier } from "../../models/interfaces";
import { AuthContext } from "../../context/auth";

export interface DeviceClassifierRow {
  code: string;
  value: string;
  isDefault: boolean;
}

interface Props {
  classifiers: Classifier[];
  data?: DeviceClassifierRow[];
  deviceType?: string;
  onChange: (data: DeviceClassifierRow[]) => void;
  readonly?: boolean;
}

const DEVICE_TYPES = {
  PC: "PC-POS",
  POS_HOTEL: "POS-HOTEL",
  H2H: "Device H2H",
  ECOM: "Ecommerce POS"
};

const CLASSIFIER_CODES = {
  CHECK_IN_OUT: "CHK_IN_CHK_OUT",
  DEVICE_CURRENCY: "DEVICE_CURR",
  REFUND: "REFUND",
  INTRA: "INTRA",
  PUNTOS: "PUNTOS"
};

const PC_CONDITIONAL_CLASSIFIERS = {
  INTRA_PLAN: "INTRA_PLAN",
  INTRA_TASA: "INTRA_TASA",
  PUNTOS_TASA: "PUNTOS_TASA"
};

export const ClassifierDeviceForm: React.FC<Props> = ({
  classifiers,
  data = [],
  deviceType,
  onChange,
  readonly
}) => {

  const [rows, setRows] = useState<DeviceClassifierRow[]>([]);

  const isReadonly = readonly === true;

  const { user } = useContext(AuthContext);

  /**
  
  Clasificadores base según deviceType.
  
  
  Aquí todavía NO aplicamos la lógica dinámica
  de INTRA / PUNTOS.
  */
  const baseClassifiers = useMemo(() => {
    if (!deviceType) return [];
    switch (deviceType) {

      case DEVICE_TYPES.H2H:
        return classifiers.filter(
          c =>
            c.code === CLASSIFIER_CODES.DEVICE_CURRENCY ||
            c.code === CLASSIFIER_CODES.REFUND
        );

      default:
        return classifiers;
    }

  }, [classifiers, deviceType]);

  /**
  
  Determina si el valor seleccionado de un clasificador
  corresponde a "SI".
  */
  const isYes = (
    classifierCode: string,
    value?: string
  ) => {
    if (!value) return false;
    const classifier = baseClassifiers.find(
      c => c.code === classifierCode
    );

    if (!classifier) return false;

    const selectedValue = classifier.validValues.find(
      v => v.valueCode === value
    );

    return selectedValue?.valueName?.toUpperCase() === "SI";

  };

  /**
  
  Clasificadores visibles.
  
  
  Para PC-POS:
  
  
  INTRA = SI
  → muestra INTRA_PLAN e INTRA_TASA
  
  
  INTRA = NO
  → oculta INTRA_PLAN e INTRA_TASA
  
  
  PUNTOS = SI
  → muestra PUNTOS_TASA
  
  
  PUNTOS = NO
  → oculta PUNTOS_TASA
  */
  const filteredClassifiers = useMemo(() => {
    if (deviceType !== DEVICE_TYPES.PC && deviceType !== DEVICE_TYPES.POS_HOTEL) {
      return baseClassifiers;
    }

    const intraRow = rows.find(
      r => r.code === CLASSIFIER_CODES.INTRA
    );

    const puntosRow = rows.find(
      r => r.code === CLASSIFIER_CODES.PUNTOS
    );

    const intraEnabled = isYes(
      CLASSIFIER_CODES.INTRA,
      intraRow?.value
    );

    const puntosEnabled = isYes(
      CLASSIFIER_CODES.PUNTOS,
      puntosRow?.value
    );

    return baseClassifiers.filter(classifier => {

      /**
       * INTRA_PLAN
       */
      if (
        classifier.code.includes(
          PC_CONDITIONAL_CLASSIFIERS.INTRA_PLAN
        )
      ) {
        return intraEnabled;
      }

      /**
       * INTRA_TASA
       */
      if (
        classifier.code.includes(
          PC_CONDITIONAL_CLASSIFIERS.INTRA_TASA
        )
      ) {
        return intraEnabled;
      }

      /**
       * PUNTOS_TASA
       */
      if (
        classifier.code.includes(
          PC_CONDITIONAL_CLASSIFIERS.PUNTOS_TASA
        )
      ) {
        return puntosEnabled;
      }

      return true;
    }).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        sensitivity: "base"
      })
    );;

  }, [
    baseClassifiers,
    deviceType,
    rows
  ]);

  /**
  
  Inicialización de clasificadores.
  */
  useEffect(() => {
    if (!deviceType || baseClassifiers.length === 0) return;
    const initialized = baseClassifiers.map(classifier => {

      const saved = data.find(
        d => d.code === classifier.code
      );

      const defaultValue = classifier.defaultValue ?? "";

      let value = saved?.value ?? defaultValue;

      /**
       * POS HOTEL → CheckInOut = SI
       */
      if (
        deviceType === DEVICE_TYPES.POS_HOTEL &&
        classifier.code === CLASSIFIER_CODES.CHECK_IN_OUT
      ) {

        const yes = classifier.validValues.find(
          v => v.valueName.toUpperCase() === "SI"
        )?.valueCode;

        if (yes) {
          value = yes;
        }
      }

      /**
       * PC y Ecommerce → CheckInOut = NO
       */
      if (
        (
          deviceType === DEVICE_TYPES.PC ||
          deviceType === DEVICE_TYPES.ECOM
        ) &&
        classifier.code === CLASSIFIER_CODES.CHECK_IN_OUT
      ) {

        const no = classifier.validValues.find(
          v => v.valueName.toUpperCase() === "NO"
        )?.valueCode;

        if (no) {
          value = no;
        }
      }

      /**
       * Panamá → Moneda default USD
       */
      if (
        classifier.code === CLASSIFIER_CODES.DEVICE_CURRENCY &&
        user?.country?.code?.toUpperCase() === "PAN"
      ) {

        const usd = classifier.validValues.find(
          v =>
            v.valueName.toUpperCase() === "USD" ||
            v.valueCode.toUpperCase() === "USD"
        )?.valueCode;

        if (usd) {
          value = usd;
        }
      }

      return {
        code: classifier.code,
        value: value || "",
        isDefault: value === defaultValue
      };

    });

    setRows(initialized);
    onChange(initialized);

  }, [
    baseClassifiers,
    deviceType
  ]);

  /**
  
  Cambio manual de un clasificador.
  
  
  También limpia automáticamente los valores
  dependientes cuando INTRA o PUNTOS cambian a NO.
  */
  const updateValue = (
    code: string,
    value: string
  ) => {
    if (isReadonly) return;
    const classifier = baseClassifiers.find(
      c => c.code === code
    );

    if (!classifier) return;

    const defaultValue = classifier.defaultValue ?? "";

    let updated = rows.map(row =>
      row.code === code
        ? {
          ...row,
          value,
          isDefault: value === defaultValue
        }
        : row
    );

    /**
     * Si estamos en PC-POS,
     * verificar cambios de INTRA y PUNTOS.
     */
    if (deviceType === DEVICE_TYPES.PC || deviceType === DEVICE_TYPES.POS_HOTEL) {

      /**
       * INTRA
       */
      if (code === CLASSIFIER_CODES.INTRA) {

        const intraEnabled = isYes(
          CLASSIFIER_CODES.INTRA,
          value
        );

        /**
         * Si INTRA no es SI,
         * limpiar INTRA_PLAN e INTRA_TASA.
         */
        if (!intraEnabled) {

          updated = updated.map(row => {

            if (
              row.code.includes(
                PC_CONDITIONAL_CLASSIFIERS.INTRA_PLAN
              ) ||
              row.code.includes(
                PC_CONDITIONAL_CLASSIFIERS.INTRA_TASA
              )
            ) {

              const dependentClassifier =
                baseClassifiers.find(
                  c => c.code === row.code
                );

              const dependentDefault =
                dependentClassifier?.defaultValue ?? "";

              return {
                ...row,
                value: "",
                isDefault: false
              };
            }

            return row;
          });
        }
      }

      /**
       * PUNTOS
       */
      if (code === CLASSIFIER_CODES.PUNTOS) {

        const puntosEnabled = isYes(
          CLASSIFIER_CODES.PUNTOS,
          value
        );

        /**
         * Si PUNTOS no es SI,
         * limpiar PUNTOS_TASA.
         */
        if (!puntosEnabled) {

          updated = updated.map(row => {

            if (
              row.code.includes(
                PC_CONDITIONAL_CLASSIFIERS.PUNTOS_TASA
              )
            ) {

              return {
                ...row,
                value: "",
                isDefault: false
              };
            }

            return row;
          });
        }
      }
    }

    setRows(updated);
    onChange(updated);

  };



  /**
 
 Solo validar los clasificadores visibles y requeridos.
 
 
 
 INTRA_PLAN e INTRA_TASA no son requeridos.
 */
  const missingRequired = filteredClassifiers.some(
    classifier => {

      /**
      
      INTRA_PLAN e INTRA_TASA son opcionales.
      */
      const isOptional =
        classifier.code.includes(
          PC_CONDITIONAL_CLASSIFIERS.INTRA_PLAN
        ) ||
        classifier.code.includes(
          PC_CONDITIONAL_CLASSIFIERS.INTRA_TASA
        );

      if (isOptional) {
        return false;
      }

      const row = rows.find(
        r => r.code === classifier.code
      );

      const hasDefault =
        classifier.defaultValue !== null &&
        classifier.defaultValue !== undefined;

      return !row?.value && !hasDefault;
    }
  );

  return (

    <div className="mb-4">

      <h6 className="mb-3">
        Clasificadores del Dispositivo
      </h6>

      <table className="table table-borderless">

        <thead className="table-light">

          <tr>

            <th style={{ width: "40%" }}>
              Clasificador
            </th>

            <th style={{ width: "50%" }}>
              Valor
            </th>

          </tr>

        </thead>

        <tbody>

          {filteredClassifiers.map(classifier => {

            const row = rows.find(
              r => r.code === classifier.code
            );

            if (!row) return null;

            const hasDefault =
              classifier.defaultValue !== null &&
              classifier.defaultValue !== undefined;

            /**
             * Check In / Check Out siempre bloqueado.
             */
            const forceCheckInOut =
              classifier.code ===
              CLASSIFIER_CODES.CHECK_IN_OUT;

            return (

              <tr key={classifier.code}>

                <td>

                  <div className="text-muted">
                    {classifier.name}
                  </div>

                </td>

                <td>

                  <select
                    className={`form-select ${!row.value && !hasDefault && !row.code.includes("INTRA_PLAN") && !row.code.includes("INTRA_TASA")
                      ? "is-invalid"
                      : ""
                      }`}
                    value={row.value || ""}
                    disabled={
                      isReadonly ||
                      forceCheckInOut
                    }
                    onChange={e =>
                      updateValue(
                        classifier.code,
                        e.target.value
                      )
                    }
                  >

                    {!hasDefault && (
                      <option value="">
                        Seleccione
                      </option>
                    )}

                    {[...classifier.validValues]
                      .sort((a, b) =>
                        a.valueName.localeCompare(b.valueName, undefined, {
                          sensitivity: "base"
                        })
                      )
                      .map(v => (
                        <option
                          key={v.valueCode}
                          value={v.valueCode}
                        >
                          {v.valueName}
                          {classifier.defaultValue === v.valueCode
                            ? " (default)"
                            : ""}
                        </option>
                      ))}

                  </select>

                </td>

              </tr>

            );

          })}

        </tbody>

      </table>

      {missingRequired && (

        <div className="text-danger mt-2">
          Debe completar todos los clasificadores.
        </div>

      )}

    </div>

  );
};